import { useNavigate } from "react-router-dom";
import DialogContent from "@mui/material/DialogContent";
import { AppDialog } from "../../components/AppDialog";
import { AppDialogHeader } from "../../components/AppDialogHeader";
import { appState } from "../../../state";
import { notifyStateChange } from "../../../stateBus";

interface ActivityRow {
  id: string;
  icon: string;
  title: string;
  subtitle: string;
  onActivate: () => void;
}

// Replaces ChatThreadScreen.tsx's old plain MUI <Menu> dropdown (anchored
// to the ✨ button, read as a generic context menu) with a proper premium
// row list - same AppDialog/AppDialogHeader shell every other page in this
// app already uses (no floating "bottom sheet" component exists here, and
// this app's own established rule is "no floating modal card surfaces
// anywhere" - see AppDialog.tsx's comment), just styled as compact,
// visual rows instead of plain outlined buttons.
//
// Icon/subtitle copy is reused verbatim from Home's own activity carousel
// (useActivityCards.ts) for each feature, not reinvented - same features,
// same framing, just a second entry point reachable from inside a
// conversation instead of only from Home.
export function ActivityLauncherSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();

  function go(action: () => void): void {
    onClose();
    action();
  }

  const rows: ActivityRow[] = [
    {
      id: "watch",
      icon: "bi-film",
      title: "Watch Together",
      subtitle: "Watch something together in sync",
      // No video is open here (this is the composer, not the Lightbox) -
      // the dedicated Watch Together page is exactly built for this case
      // (pick a video, then start), replacing the old "toast + dump you in
      // /gallery" workaround that predated that page existing.
      onActivate: () => navigate("/watch-together"),
    },
    {
      id: "game",
      icon: "bi-controller",
      title: "Play a Game",
      subtitle: "Questions, Truth or Dare & Spicy decks",
      onActivate: () => {
        appState.gameDialogOpen = true;
        notifyStateChange();
      },
    },
    {
      id: "party",
      icon: "bi-music-note-beamed",
      title: "Listen Together",
      subtitle: "Synchronized music, together",
      onActivate: () => {
        appState.partyInviteDialogOpen = true;
        notifyStateChange();
      },
    },
    {
      id: "wheel",
      icon: "bi-suit-heart-fill",
      title: "Couple Wheel",
      subtitle: "Spin together, land on the same result",
      onActivate: () => navigate("/couple-wheel"),
    },
  ];

  return (
    <AppDialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <AppDialogHeader icon="bi-stars" title="Things to do together" onBack={onClose} />
      <DialogContent sx={{ pt: 1 }}>
        <p className="activity-launcher-subtitle">Choose something for both of you.</p>
        <div className="activity-launcher-rows">
          {rows.map((row) => (
            <button type="button" key={row.id} className="activity-launcher-row" onClick={() => go(row.onActivate)}>
              <span className="activity-launcher-row-icon">
                <i className={`bi ${row.icon}`} aria-hidden="true" />
              </span>
              <span className="activity-launcher-row-body">
                <span className="activity-launcher-row-title">{row.title}</span>
                <span className="activity-launcher-row-subtitle">{row.subtitle}</span>
              </span>
              <i className="bi bi-chevron-right activity-launcher-row-chevron" aria-hidden="true" />
            </button>
          ))}
        </div>
      </DialogContent>
    </AppDialog>
  );
}
